import { WebViewerInstance } from '@pdftron/webviewer';
import cn from 'classnames';
import { JSONPatchDocument } from 'immutable-json-patch';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useRouteMatch } from 'react-router-dom';

import { generateHeaders, rewriteUrl } from 'api/apiReqSagaCreator/httpRequest';
import { ExternalFromUiParams } from 'app/routes/paths';
import UpdateIcon from 'assets/icon/update-doc.svg';
import SpinnerIcon from 'assets/img/spinner.svg';
import { useApplicationErrorHandling } from 'hooks/useApplicationErrorHandling';
import { useSafeHandler } from 'hooks/useSafeHandler';
import { ApplicationError } from 'types/errorHandling';

import {
  checkDocumentUrl,
  useWebViewer,
  useWebviewerModularUI,
  useWebviewerSettings,
} from '../reusableWebviewer/useWebviewer';
import sm from './styles.module.scss';

type OnUpdateDocumentClick = (file: Blob) => void;

type OnSetRef = (
  instance: WebViewerInstance & { loadDocument: () => void }
) => void;

export interface Props {
  docUrl?: string | Blob;
  docName?: string;
  onSetRef?: OnSetRef;
  withFullHeight?: boolean;
  onUpdateDocumentClick?: OnUpdateDocumentClick;
  usePrivateViewer?: boolean;
}

const SESSION_STORAGE_PDFTRON_FITMODE = 'PDFTronViewerFitMode';

type LoadDocument = () => Promise<void>;

const PdfTronViewer = ({
  onSetRef,
  onUpdateDocumentClick,
  docUrl,
  docName,
  withFullHeight,
  usePrivateViewer,
}: Props) => {
  const [annotationsWereLoaded, setAnnotationsWereLoaded] = useState(false);

  const viewerElementRef = useRef<Nullable<HTMLDivElement>>(null);
  const viewerInstance = useWebViewer(viewerElementRef, usePrivateViewer);

  const handleAppErr = useApplicationErrorHandling();

  // load and store pdf viewer fit mode
  useEffect(() => {
    if (!(viewerInstance && annotationsWereLoaded)) return;

    const previousFitMode =
      window.sessionStorage.getItem(SESSION_STORAGE_PDFTRON_FITMODE) ??
      'FitWidth';

    if (['FitWidth', 'FitPage'].includes(previousFitMode))
      viewerInstance.UI.setFitMode(previousFitMode);
    else viewerInstance.UI.setZoomLevel(Number(previousFitMode));

    return () => {
      const currentFitMode = viewerInstance.UI.getFitMode();

      if (currentFitMode === 'Zoom')
        window.sessionStorage.setItem(
          SESSION_STORAGE_PDFTRON_FITMODE,
          viewerInstance.UI.getZoomLevel().toString()
        );
      else
        window.sessionStorage.setItem(
          SESSION_STORAGE_PDFTRON_FITMODE,
          currentFitMode
        );
    };
  }, [annotationsWereLoaded, viewerInstance]);

  const { params } = useRouteMatch<ExternalFromUiParams>();

  const loadDocument: LoadDocument = useCallback(async () => {
    if (!viewerInstance) return;

    if (!docUrl) {
      viewerInstance.UI.closeDocument();
      return;
    }

    setAnnotationsWereLoaded(false);
    viewerInstance.Core.documentViewer.addEventListener(
      'documentLoaded',
      () => {
        setAnnotationsWereLoaded(true);
      },
      { once: true }
    );

    let finalDocUrl = docUrl;
    if (typeof docUrl === 'string') {
      const cacheBustedDocUrl = new URL(docUrl);
      cacheBustedDocUrl.searchParams.append('cache_bust', crypto.randomUUID());

      finalDocUrl = await checkDocumentUrl(
        rewriteUrl(cacheBustedDocUrl.toString()),
        params.token
      );
    }

    viewerInstance.UI.loadDocument(finalDocUrl, {
      filename: docName?.includes('/') ? docName?.split('/').reverse()[0] : '',
      customHeaders: {
        ...(typeof finalDocUrl === 'string'
          ? generateHeaders(finalDocUrl)
          : {}),
      },
    });
  }, [viewerInstance, docUrl, params.token, docName]);

  const updateDocument = useSafeHandler(
    useCallback(async () => {
      if (!(onUpdateDocumentClick && viewerInstance)) return;

      const { documentViewer, annotationManager, SaveOptions } =
        viewerInstance.Core;

      const receivedDoc = documentViewer.getDocument();
      const xfdfString = await annotationManager.exportAnnotations();

      const data = await receivedDoc.getFileData({
        xfdfString,
        flags: SaveOptions.LINEARIZED,
        downloadType: 'pdf',
      });

      const blob = new Blob([data], { type: 'application/pdf' });
      onUpdateDocumentClick(blob);
    }, [viewerInstance, onUpdateDocumentClick])
  );

  useWebviewerModularUI(
    viewerInstance,
    ...useMemo(() => {
      const updateFileButtonName = 'updateFileButton';
      const updateFileButtonHandler = 'updateFileButtonHandler';
      const updateFileButtonPendingState = 'pending';
      const updateFileButtonUpdatingState = 'updating';

      const updateFileButtonNoOpHandler = 'updateFileButtonNoOpHandler';

      const mainMenuUpdateFileButtonName = 'mainMenuUpdateFileButton';
      const mainMenuUpdateFileButtonHandler = 'mainMenuUpdateFileButtonHandler';

      const functionMap = {
        [updateFileButtonHandler]: async (
          updateButtonState: (newState: string) => void
        ) => {
          updateButtonState(updateFileButtonUpdatingState);

          await updateDocument();

          updateButtonState(updateFileButtonPendingState);
        },
        [updateFileButtonNoOpHandler]: () => {},
        [mainMenuUpdateFileButtonHandler]: () => {
          updateDocument();
        },
      };

      const updateDocumentButtonPatches: JSONPatchDocument = [
        {
          op: 'add',
          path: `/modularComponents/${updateFileButtonName}`,
          value: {
            type: 'statefulButton',
            dataElement: updateFileButtonName,
            initialState: updateFileButtonPendingState,
            states: {
              [updateFileButtonPendingState]: {
                img: UpdateIcon,
                title: 'Update Document',
                onClick: updateFileButtonHandler,
              },
              [updateFileButtonUpdatingState]: {
                img: SpinnerIcon,
                title: 'Document updating...',
              },
            },
            mount: updateFileButtonNoOpHandler,
            unmount: updateFileButtonNoOpHandler,
          },
        },
        {
          op: 'add',
          path: '/modularComponents/groupedLeftHeaderButtons/items/1',
          value: updateFileButtonName,
        },
        {
          op: 'add',
          path: `/modularComponents/${mainMenuUpdateFileButtonName}`,
          value: {
            label: 'Update Document',
            icon: UpdateIcon,
            dataElement: mainMenuUpdateFileButtonName,
            onClick: mainMenuUpdateFileButtonHandler,
            type: 'customButton',
          },
        },
        {
          op: 'add',
          path: '/flyouts/MainMenuFlyout/items/0',
          value: mainMenuUpdateFileButtonName,
        },
      ];

      const patch: JSONPatchDocument = [
        ...(onUpdateDocumentClick ? updateDocumentButtonPatches : []),
      ];

      return [patch, functionMap] as const;
    }, [onUpdateDocumentClick, updateDocument])
  );

  useWebviewerSettings(
    viewerInstance,
    false,
    ['ThumbnailMerging', 'ThumbnailReordering'],
    ['deletePage', 'insertPage', 'replacePage', 'thumbDelete']
  );

  useEffect(() => {
    if (viewerInstance) {
      loadDocument().then(
        () => {},
        (e) => {
          handleAppErr(
            ApplicationError.FromError(e, 'Failed to load document')
          );
        }
      );
      if (onSetRef) {
        onSetRef({ ...viewerInstance, loadDocument });
      }
    }
  }, [viewerInstance, loadDocument, onSetRef, handleAppErr]);

  return (
    <>
      <div
        className={cn(sm.Viewer, {
          [sm.Viewer_FullHeight]: withFullHeight,
        })}
        ref={viewerElementRef}
      />
    </>
  );
};

export default memo(PdfTronViewer);
